import { useMemo, useState } from "react";
import { Copy, Check, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Deterministic, instant outbound-lead email draft built from the scan's hosting data.
export function buildPitchEmail({ domain, hosting = {}, scopeLabel }) {
  const host = hosting.platform || hosting.hostLabel;
  const fw = hosting.framework && hosting.framework !== "None / Vanilla" ? ` (${hosting.framework})` : "";
  const deps = (hosting.dependencies || []).map((d) => d.name);
  const savings = hosting.savings || {};

  const subject = `Modernizing ${domain}`;
  const lines = [
    "Hi there,",
    "",
    `I ran a quick, no-touch scan of ${domain} and wanted to share what I found.`,
    "",
    host
      ? `Your site is currently built on ${host}${fw}.`
      : `Your site${fw ? ` is built with${fw}` : ""}.`,
  ];
  if (deps.length) {
    lines.push(`It also relies on ${deps.slice(0, 6).join(", ")}${deps.length > 6 ? ", and more" : ""}.`);
  }
  lines.push(
    "",
    "We can move & modernize it onto Emergent with zero downtime — carrying everything above over, hardening security, and consolidating the tools you pay for into one managed place.",
  );
  if (scopeLabel) lines.push("", `Based on the scan, this looks like a ${scopeLabel.toLowerCase()}.`);
  if (savings.totalMonthly > 0) {
    lines.push(
      "",
      `Consolidating this stack could save roughly $${Math.round(savings.totalMonthly)}/mo (about $${Math.round(savings.totalAnnual)}/yr) — a rough estimate from the scan.`,
    );
  }
  lines.push(
    "",
    "If you're open to it, I'd love to put together a free plan and quote — just reply here.",
    "",
    "Best,",
  );
  return { subject, body: lines.join("\n") };
}

export default function MigrationEmailDialog({ open, onOpenChange, domain, hosting, scopeLabel }) {
  const [copied, setCopied] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [logged, setLogged] = useState(false);
  const { subject, body } = useMemo(
    () => buildPitchEmail({ domain, hosting, scopeLabel }),
    [domain, hosting, scopeLabel],
  );
  const mailto = `mailto:${encodeURIComponent(recipient || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const logPitch = (channel) => {
    if (logged) return;
    setLogged(true);
    const savings = hosting?.savings || {};
    api.post("/leads/pitch", {
      domain,
      platform: hosting?.platform || hosting?.hostLabel || "",
      recipient: recipient || "",
      savingsMonthly: savings.totalMonthly || 0,
      dependencies: (hosting?.dependencies || []).map((d) => d.name),
      channel,
    }).catch(() => {});
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopied(true);
      toast.success("Email copied — logged as a lead");
      logPitch("copy");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the text manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setLogged(false); setCopied(false); } onOpenChange(v); }}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="email-pitch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Mail size={18} /> Email this pitch</DialogTitle>
          <DialogDescription className="text-zinc-400">
            A ready-to-send note personalized from the scan of <b className="text-white">{domain}</b>. Copy it or open it in your email app.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <div className="text-xs text-zinc-500 mb-1">To (optional)</div>
            <input
              data-testid="email-pitch-recipient"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="owner@business.com"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#007AFF]/50"
            />
          </div>
          <div className="text-xs text-zinc-500">Subject</div>
          <div data-testid="email-pitch-subject" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{subject}</div>
          <textarea
            data-testid="email-pitch-body"
            readOnly
            value={body}
            rows={11}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none font-mono leading-relaxed focus:outline-none focus:border-[#007AFF]/50"
          />
          <div className="flex items-center gap-2">
            <button data-testid="email-pitch-copy" onClick={copy} className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors">
              {copied ? <><Check size={15} className="text-[#32D74B]" /> Copied</> : <><Copy size={15} /> Copy email</>}
            </button>
            <a data-testid="email-pitch-open" href={mailto} onClick={() => logPitch("email")} className="flex-1 btn-brand text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2">
              <ExternalLink size={15} /> Open in email
            </a>
          </div>
          <p className="text-[11px] text-zinc-600 text-center">Sending logs this as a warm lead in your admin inbox.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
